import { useEffect, useState } from "react";

import {
    BadgeFillDto,
    BadgeFillKind,
    BadgePlacement,
    MenuItemBadgeDto,
} from "services/menuItemBadges/restModels";
import { MenuItemBadgeRequestDto } from "services/menuItemBadges/restRequests";
import {
    solidFill,
    suggestTextColor,
    withFillKind,
} from "shared/lib/badgeFill";
import { hasLocalizedValue, setLocalizedValue, trimLocalizedMap } from "shared/lib/localizedMap";

import { DEFAULT_BADGE_COLOR } from "features/MenuItemBadges/constants";

export interface UseMenuItemBadgeFormResult {
    title: Record<string, string>;
    setTitle: (value: string, lang: string) => void;
    fill: BadgeFillDto;
    setFillKind: (kind: BadgeFillKind) => void;
    setColorFrom: (color: string) => void;
    setColorTo: (color: string) => void;
    setAngleDeg: (angleDeg: number) => void;
    textColor: string;
    setTextColor: (color: string) => void;
    placement: BadgePlacement;
    setPlacement: (placement: BadgePlacement) => void;
    isEnabled: boolean;
    setIsEnabled: (isEnabled: boolean) => void;
    isValid: boolean;
    buildRequest: () => MenuItemBadgeRequestDto;
}

const emptyFill = (): BadgeFillDto => solidFill(DEFAULT_BADGE_COLOR);

/*
* Форма бейджа. Цвет текста подставляется по заливке, но только пока его не тронули руками:
* иначе выбранный вручную тёмный текст откатывался бы к белому на каждой правке фона.
* */
export const useMenuItemBadgeForm = (
    badge: MenuItemBadgeDto | null,
    open: boolean
): UseMenuItemBadgeFormResult => {
    const [title, setTitleMap] = useState<Record<string, string>>({});
    const [fill, setFill] = useState<BadgeFillDto>(emptyFill());
    const [textColor, setTextColorValue] = useState<string>("#FFFFFF");
    const [isTextColorTouched, setIsTextColorTouched] = useState<boolean>(false);
    const [placement, setPlacement] = useState<BadgePlacement>("OverPhoto");
    const [isEnabled, setIsEnabled] = useState<boolean>(true);

    useEffect(() => {
        if (!open) return;

        setTitleMap(badge ? { ...badge.title } : {});
        setFill(badge ? badge.fill : emptyFill());
        setTextColorValue(badge ? badge.textColor : "#FFFFFF");
        // У сохранённого бейджа цвет текста уже выбран — не переподбираем его под фон.
        setIsTextColorTouched(!!badge);
        setPlacement(badge ? badge.placement : "OverPhoto");
        setIsEnabled(badge ? badge.isEnabled : true);
    }, [badge, open]);

    const applyFill = (next: BadgeFillDto) => {
        setFill(next);
        if (!isTextColorTouched) setTextColorValue(suggestTextColor(next));
    };

    return {
        title,
        setTitle: (value, lang) => setTitleMap((prev) => setLocalizedValue(prev, lang, value)),

        fill,
        setFillKind: (kind) => applyFill(withFillKind(fill, kind)),
        setColorFrom: (color) => applyFill({ ...fill, colorFrom: color }),
        setColorTo: (color) => applyFill({ ...fill, colorTo: color }),
        setAngleDeg: (angleDeg) => setFill({ ...fill, angleDeg }),

        textColor,
        setTextColor: (color) => {
            setIsTextColorTouched(true);
            setTextColorValue(color);
        },

        placement,
        setPlacement,
        isEnabled,
        setIsEnabled,

        isValid: hasLocalizedValue(title),
        buildRequest: () => ({
            title: trimLocalizedMap(title),
            fill,
            textColor,
            placement,
            isEnabled,
        }),
    };
};
